import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useCallback } from 'react'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useLayoutContext } from '@src/components/pl-page-context'
import { useIaImpressionRef } from '@src/components/widgets/use-ia-impression'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { ProductComparisonCarouselDSWidget } from './product-comparison-carousel-ds-widget'
import { ProductComparisonCarouselLegacy } from './product-comparison-carousel-legacy'

import type { ProductComparisonCarouselWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Telemetry action id (session-stream `entity_id`) — a stable identifier, not user-facing copy.
const PRODUCT_COMPARISON_ACTION = 'product_comparison_section'

const ProductComparisonCarouselComponent = (props: PLTypedWidgetProps<ProductComparisonCarouselWidgetData>) => {
  // IA-only `product_comparison_section` impression on scroll-into-view — mirrors the non-PL
  // ProductComparisonSection (`entity_id: product_comparison_section`, context `{ productId }` only),
  // emitted InternalAnalytics-only per the PL directive. The wrapper div carries the ref (matches the
  // non-PL section's own `<div ref>` wrapper) around whichever variant renders.
  const productId = useLayoutContext()?.pdp?.productIds?.productId
  const getImpressionParams = useCallback(
    () => ({
      action: PRODUCT_COMPARISON_ACTION,
      context: { productId },
    }),
    [productId],
  )
  const impressionRef = useIaImpressionRef(getImpressionParams)

  const content = shouldUseDesignSystem() ? (
    <ProductComparisonCarouselDSWidget {...props} />
  ) : (
    <ProductComparisonCarouselLegacy {...props} />
  )
  // Empty section (no comparison items): both variants render `null`. Return the bare variant so we emit
  // no wrapper div and fire no impression — preserving the widget's "renders nothing when data is absent"
  // contract and avoiding an impression for a section the user never sees.
  const hasItems = (props.data.productComparisonCarouselData?.items ?? []).length > 0
  if (!hasItems) return content
  return <div ref={impressionRef}>{content}</div>
}

export const ProductComparisonCarouselWidget: PLTypedWidgetInterface<
  ProductComparisonCarouselWidgetData,
  WidgetType.PRODUCT_COMPARISON_CAROUSEL
> = {
  id: WidgetType.PRODUCT_COMPARISON_CAROUSEL,
  errorBoundary: PLBaseErrorBoundary,
  render: ProductComparisonCarouselComponent,
}
